import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, HeartPulse, Activity } from "lucide-react";
import { AthleteSelect } from "@/components/AthleteSelect";
import { toast } from "sonner";
import { displayName, initials, sortAthletes, matchesSearch } from "@/lib/athleteName";

const STATUS_COLORS = {
  attivo: "bg-red-50 text-red-700 border-red-200",
  riabilitazione: "bg-amber-50 text-amber-700 border-amber-200",
  return_to_play: "bg-blue-50 text-blue-700 border-blue-200",
  chiuso: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const SEVERITY_COLORS = {
  lieve: "bg-zinc-100 text-zinc-700",
  moderata: "bg-amber-100 text-amber-800",
  grave: "bg-red-100 text-red-800",
};

const initialForm = {
  athlete_id: "",
  date: new Date().toISOString().slice(0, 10),
  type: "muscolare",
  body_part: "",
  severity: "lieve",
  status: "attivo",
  estimated_return: "",
  diagnosis: "",
  notes: "",
};

export default function Injuries() {
  const [items, setItems] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [filter, setFilter] = useState("attivi");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editing, setEditing] = useState(null);
  const [newUpdate, setNewUpdate] = useState("");

  const load = async () => {
    const [inj, ath] = await Promise.all([api.get("/injuries"), api.get("/athletes?include_archived=1")]);
    setItems(inj.data);
    setAthletes(ath.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (!form.athlete_id) return toast.error("Seleziona un atleta");
      await api.post("/injuries", form);
      toast.success("Infortunio registrato");
      // Update athlete status if active
      if (form.status === "attivo") {
        const ath = athletes.find((a) => a.id === form.athlete_id);
        if (ath) await api.put(`/athletes/${ath.id}`, { ...ath, status: "infortunato" });
      }
      setOpen(false);
      setForm(initialForm);
      load();
    } catch { toast.error("Errore"); }
  };

  const updateStatus = async (inj, status) => {
    const patched = { ...inj, status };
    if (status === "chiuso" && !inj.return_date) patched.return_date = new Date().toISOString().slice(0, 10);
    await api.put(`/injuries/${inj.id}`, patched);
    if (status === "chiuso") {
      const ath = athletes.find((a) => a.id === inj.athlete_id);
      if (ath) await api.put(`/athletes/${ath.id}`, { ...ath, status: "disponibile" });
    } else if (status === "return_to_play") {
      const ath = athletes.find((a) => a.id === inj.athlete_id);
      if (ath) await api.put(`/athletes/${ath.id}`, { ...ath, status: "return_to_play" });
    }
    toast.success("Aggiornato");
    load();
  };

  const addUpdate = async () => {
    if (!newUpdate || !editing) return;
    const upd = { date: new Date().toISOString().slice(0, 10), note: newUpdate, status: editing.status };
    const patched = { ...editing, updates: [...(editing.updates || []), upd] };
    await api.put(`/injuries/${editing.id}`, patched);
    setNewUpdate("");
    setEditing(patched);
    load();
  };

  const filtered = filter === "attivi"
    ? items.filter((i) => i.status !== "chiuso")
    : filter === "chiusi"
    ? items.filter((i) => i.status === "chiuso")
    : items;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Infortuni</h1>
          <p className="text-zinc-500 mt-1">Gestione infortuni e return to play.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-injury-btn" className="h-12 bg-black hover:bg-zinc-800"><Plus className="w-4 h-4 mr-2" />Nuovo infortunio</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Registra infortunio</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>Atleta</Label>
                <AthleteSelect
                  value={form.athlete_id}
                  onChange={(v) => setForm({ ...form, athlete_id: v })}
                  athletes={athletes}
                  testid="injury-athlete"
                  placeholder="Seleziona atleta"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div>
                  <Label>Tipo</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="muscolare">Muscolare</SelectItem>
                      <SelectItem value="articolare">Articolare</SelectItem>
                      <SelectItem value="trauma">Trauma</SelectItem>
                      <SelectItem value="altro">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Parte del corpo</Label><Input data-testid="injury-body" placeholder="es. Ginocchio destro" value={form.body_part} onChange={(e) => setForm({ ...form, body_part: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Gravità</Label>
                  <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lieve">Lieve</SelectItem>
                      <SelectItem value="moderata">Moderata</SelectItem>
                      <SelectItem value="grave">Grave</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Rientro previsto</Label><Input type="date" value={form.estimated_return} onChange={(e) => setForm({ ...form, estimated_return: e.target.value })} /></div>
              </div>
              <div><Label>Diagnosi</Label><Input value={form.diagnosis} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} /></div>
              <div><Label>Note</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter><Button data-testid="injury-save" onClick={save} className="bg-black hover:bg-zinc-800">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-2">
        {["attivi", "chiusi", "tutti"].map((f) => (
          <button
            key={f}
            data-testid={`filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-md text-sm font-semibold capitalize ${filter === f ? "bg-black text-white" : "bg-white border border-zinc-200"}`}
          >{f}</button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((inj) => {
          const ath = athletes.find((a) => a.id === inj.athlete_id);
          return (
            <button key={inj.id} onClick={() => { setEditing(inj); }} data-testid={`injury-${inj.id}`} className="text-left bg-white border border-zinc-200 rounded-md p-5 hover:border-zinc-400 transition-colors">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{inj.date}</div>
                  <div className="font-heading font-semibold">{displayName(ath)}</div>
                </div>
                <HeartPulse className="w-4 h-4 text-zinc-400" />
              </div>
              <div className="text-sm text-zinc-700">{inj.body_part || "—"}</div>
              <div className="text-xs text-zinc-500 font-mono mt-1">{inj.type}</div>
              <div className="flex gap-2 mt-3">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${SEVERITY_COLORS[inj.severity]}`}>{inj.severity}</span>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${STATUS_COLORS[inj.status]}`}>{inj.status.replace("_", " ")}</span>
              </div>
              {inj.estimated_return && <div className="text-xs text-zinc-500 font-mono mt-2">Rientro: {inj.estimated_return}</div>}
            </button>
          );
        })}
        {filtered.length === 0 && <div className="col-span-full text-zinc-500 py-8 text-center">Nessun infortunio registrato.</div>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl">
          {editing && (
            <>
              <DialogHeader><DialogTitle>Infortunio · {displayName(athletes.find((a) => a.id === editing.athlete_id))}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-zinc-500 text-xs uppercase tracking-wider">Data</span><div className="font-mono">{editing.date}</div></div>
                  <div><span className="text-zinc-500 text-xs uppercase tracking-wider">Parte</span><div>{editing.body_part}</div></div>
                  <div><span className="text-zinc-500 text-xs uppercase tracking-wider">Tipo</span><div>{editing.type}</div></div>
                  <div><span className="text-zinc-500 text-xs uppercase tracking-wider">Gravità</span><div className="capitalize">{editing.severity}</div></div>
                </div>
                {editing.diagnosis && <div><Label>Diagnosi</Label><div className="text-sm">{editing.diagnosis}</div></div>}
                {editing.notes && <div><Label>Note</Label><div className="text-sm text-zinc-700">{editing.notes}</div></div>}

                <div>
                  <Label>Cambia stato</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {["attivo", "riabilitazione", "return_to_play", "chiuso"].map((s) => (
                      <button
                        key={s}
                        onClick={() => updateStatus(editing, s)}
                        data-testid={`inj-status-${s}`}
                        className={`tap-target px-3 py-2 text-sm rounded-md border ${editing.status === s ? "bg-black text-white border-black" : "bg-white border-zinc-200"}`}
                      >{s.replace("_", " ")}</button>
                    ))}
                  </div>
                </div>

                <div className="border-t border-zinc-200 pt-3">
                  <Label>Aggiornamenti</Label>
                  <div className="space-y-2 mt-2 max-h-40 overflow-y-auto no-scrollbar">
                    {(editing.updates || []).map((u, i) => (
                      <div key={i} className="text-sm bg-zinc-50 border border-zinc-200 rounded-md p-2">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{u.date} · {u.status}</div>
                        <div>{u.note}</div>
                      </div>
                    ))}
                    {(editing.updates || []).length === 0 && <div className="text-xs text-zinc-500">Nessun aggiornamento.</div>}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Input placeholder="Nuova nota…" value={newUpdate} onChange={(e) => setNewUpdate(e.target.value)} />
                    <Button data-testid="add-injury-update" onClick={addUpdate} className="bg-black hover:bg-zinc-800">Aggiungi</Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
